// apollo-client.ts
import {
  ApolloClient,
  InMemoryCache,
  createHttpLink,
  ApolloLink,
  fromPromise,
  HttpLink,
  makeVar,
} from "@apollo/client";

import { onError } from "@apollo/client/link/error";
import { setContext } from "@apollo/client/link/context";
import { REFRESH_TOKEN_QUERY } from "./authQueries";

let isRefreshing = false;
let pendingRequests: Function[] = [];

const resolvePendingRequests = () => {
  pendingRequests.forEach((callback) => callback());
  pendingRequests = [];
};

// Function to handle token refresh
export async function refreshToken(httpLink: ApolloLink) {
  const client = new ApolloClient({
    link: httpLink,
    cache: new InMemoryCache(),
  });

  const { data } = await client.query({
    query: REFRESH_TOKEN_QUERY,
    fetchPolicy: "no-cache",
  });

  const newAccessToken = data?.getAccessToken?.access_token;
  if (!newAccessToken) {
    throw new Error("No new access token received");
  }

  // Do not persist access tokens to localStorage; rely on HttpOnly cookies.
  return newAccessToken;
}

export function createApolloServerClient(
  token?: string,
  extraHeaders: Record<string, string> = {}
) {
  const backendUrl = process.env.NEXT_PUBLIC_BACKEND_URI + "/graphql";

  const httpLink = new HttpLink({
    uri: (operation) => {
      const url = new URL(backendUrl);
      const operationName = operation.operationName || "anonymous";
      url.searchParams.set("operationName", operationName);
      return url.toString();
    },
    fetch,
    headers: {
      ...extraHeaders,
    },
  });

  const authLink = setContext((_, { headers }) => ({
    headers: {
      ...headers,
      Authorization: token ? `Bearer ${token}` : "",
    },
  }));

  return new ApolloClient({
    ssrMode: true,
    link: authLink.concat(httpLink),
    cache: new InMemoryCache(),
    defaultOptions: {
      query: {
        fetchPolicy: "no-cache",
      },
      watchQuery: {
        fetchPolicy: "no-cache",
        errorPolicy: "ignore",
      },
      mutate: {
        fetchPolicy: "no-cache",
      },
    },
  });
}

// This function will be used by both initial setup and auth context
export const createApolloClient = (
  _token?: string | null
): ApolloClient<any> => {
  const backendUrl = process.env.NEXT_PUBLIC_BACKEND_URI + "/graphql";

  const httpLink = createHttpLink({
    uri: (operation) => {
      const url = new URL(backendUrl);
      const operationName = operation.operationName || "anonymous";
      url.searchParams.set("query", operationName);
      return url.toString();
    },
    credentials: "include", // Important for sending cookies
  });

  const errorLink = onError(
    ({ graphQLErrors, networkError, operation, forward }) => {
      // Check for authentication errors in both GraphQL and Network errors
      const isAuthError =
        (networkError &&
          "statusCode" in networkError &&
          networkError.statusCode === 401) ||
        graphQLErrors?.some(
          (err) =>
            err.extensions?.code === "UNAUTHENTICATED" ||
            err.message.includes("token") ||
            err.message.includes("authentication")
        );

      if (isAuthError) {
        // Check if user was previously authenticated (has user in localStorage)
        // Only try to refresh token if user was authenticated
        const wasAuthenticated = !!localStorage.getItem("user");

        // If user was never authenticated, don't try to refresh token
        // Just let the error pass through (for public pages like profiles)
        if (!wasAuthenticated) {
          return forward(operation);
        }

        if (!isRefreshing) {
          isRefreshing = true;

          return fromPromise(
            refreshToken(httpLink)
              .then((newAccessToken) => {
                resolvePendingRequests();
                return newAccessToken;
              })
              .catch((error) => {
                console.error("Token refresh failed:", error);
                pendingRequests = [];
                // If refresh token is invalid, log out the user
                localStorage.removeItem("auth_token");
                localStorage.removeItem("user");
                window.location.href = "/login"; // Redirect to login
                return null;
              })
              .finally(() => {
                isRefreshing = false;
              })
          ).flatMap(() => {
            // Retry the failed operation; cookies are included automatically
            return forward(operation);
          });
        } else {
          return fromPromise(
            new Promise((resolve) => {
              pendingRequests.push(() => resolve(null));
            })
          ).flatMap(() => {
            // Once refresh has completed, retry the operation.
            return forward(operation);
          });
        }
      }

      if (graphQLErrors) {
        graphQLErrors.forEach(({ message, locations, path }) => {
          console.error(
            `[GraphQL error]: Message: ${message}, Location: ${JSON.stringify(locations)}, Path: ${path}`
          );
        });
      }

      if (networkError) {
        console.error("[Network error]:", networkError);
      }
    }
  );

  return new ApolloClient({
    link: ApolloLink.from([errorLink, httpLink]),
    cache: new InMemoryCache(),
    defaultOptions: {
      watchQuery: {
        fetchPolicy: "network-only",
        errorPolicy: "all",
      },
      query: {
        fetchPolicy: "network-only",
        errorPolicy: "all",
      },
      mutate: {
        errorPolicy: "all",
      },
    },
    connectToDevTools: true,
  });
};

export default createApolloClient;
// src/cache.ts
